import React from 'react';
import { Helmet } from 'react-helmet';

// תבנית בסיסית עבור נתוני SEO לכל עמוד
export interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  keywords?: string;
  structuredData?: Record<string, any>;
  language?: string;
  alternateLanguages?: Array<{
    hrefLang: string;
    href: string;
  }>;
}

// רכיב ה-SEO שיוסף לכל עמוד
export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage = "/og-image.png", // ברירת מחדל לתמונת OG
  keywords,
  structuredData,
  language = "he",
  alternateLanguages,
}) => {
  // כתובת האתר המלאה - יש להחליף ל-URL הייצורי של האתר שלך
  const siteUrl = "https://www.yourwebsite.com";
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  const fullOgImageUrl = ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`;

  // נתוני structured data בסיסיים עבור הארגון (schema.org)
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "שלומית ז'בנר",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "054-4728562",
      "contactType": "customer service",
      "availableLanguage": ["Hebrew"]
    },
    "sameAs": [
      "https://www.facebook.com/your-facebook-page",
      "https://www.linkedin.com/company/your-linkedin-page",
      "https://www.instagram.com/your-instagram-page"
    ]
  };

  // מיזוג נתוני structured data ספציפיים לעמוד עם הנתונים הבסיסיים
  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Meta Tags בסיסיים */}
      <html lang={language} dir="rtl" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImageUrl} />
      <meta property="og:site_name" content="שלומית ז'בנר " />
      <meta property="og:locale" content={language === 'he' ? 'he_IL' : 'en_US'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImageUrl} />
      
      {/* Alternate Language Links */}
      {alternateLanguages?.map((alt) => (
        <link 
          key={alt.hrefLang}
          rel="alternate" 
          hrefLang={alt.hrefLang} 
          href={alt.href.startsWith("http") ? alt.href : `${siteUrl}${alt.href}`}
        />
      ))}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* תמיכה בבקשות מתבניות חיפוש מובנות */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#1a56db" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="שלומית ז'בנר " />
      
      {/* מניעת שמירה במטמון דפים - הסר אם אינו נדרש */}
      <meta httpEquiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
      <meta httpEquiv="Pragma" content="no-cache" />
      <meta httpEquiv="Expires" content="0" />
    </Helmet>
  );
};

// תבניות Structured Data נפוצות

// LocalBusiness ל-Schema.org
export const generateLocalBusinessSchema = (data: {
  name: string;
  image: string;
  telephone: string;
  priceRange: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    postalCode: string;
    addressCountry: string;
  };
  geo: {
    latitude: number;
    longitude: number;
  };
  openingHours: string[];
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": data.name,
    "image": data.image,
    "telephone": data.telephone,
    "priceRange": data.priceRange,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": data.address.streetAddress,
      "addressLocality": data.address.addressLocality,
      "postalCode": data.address.postalCode,
      "addressCountry": data.address.addressCountry
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": data.geo.latitude,
      "longitude": data.geo.longitude
    },
    "openingHoursSpecification": data.openingHours.map(hours => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": hours.split(": ")[0],
      "opens": hours.split(": ")[1].split("-")[0],
      "closes": hours.split(": ")[1].split("-")[1]
    }))
  };
};

// Service ל-Schema.org עבור שירותים
export const generateServiceSchema = (data: {
  name: string;
  serviceType: string;
  description: string;
  provider: string;
  areaServed: string;
  image?: string;
  offers?: {
    price: number;
    priceCurrency: string;
  };
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": data.name,
    "serviceType": data.serviceType,
    "description": data.description,
    "provider": {
      "@type": "Organization",
      "name": data.provider
    },
    "areaServed": data.areaServed,
    ...(data.image && { "image": data.image }),
    ...(data.offers && {
      "offers": {
        "@type": "Offer",
        "price": data.offers.price,
        "priceCurrency": data.offers.priceCurrency
      }
    })
  };
};

// FAQ ל-Schema.org
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// BreadcrumbList ל-Schema.org
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

export default SEO;